import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API, errMsg } from "@/lib/api";

const TOPICS = [["general", "General question"], ["account", "Account or sign-in"], ["courses", "Courses and learning"],
  ["employers", "Hiring on Crowlearn"], ["other", "Something else"]];
const EMPTY = { name: "", email: "", topic: "general", message: "", website: "" };

export function ContactForm() {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, f);
      setSent(true);
      setF(EMPTY);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) return (
    <div className="mt-10 rounded-2xl border border-emerald-300 bg-emerald-50 p-6" role="status" data-testid="contact-success">
      <CheckCircle2 className="h-6 w-6 text-emerald-700" />
      <p className="mt-3 font-semibold text-slate-900">Thanks — your message has been sent.</p>
      <p className="mt-1 text-sm text-slate-700">We'll reply to the email address you gave us.</p>
      <Button variant="outline" className="mt-4" onClick={() => setSent(false)} data-testid="contact-send-another">Send another message</Button>
    </div>
  );

  return (
    <form onSubmit={submit} className="mt-10 space-y-4 rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="contact-form">
      <h2 className="text-xl font-bold text-slate-900">Send us a message</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5"><Label htmlFor="c-name">Your name</Label><Input id="c-name" value={f.name} onChange={(e) => set("name", e.target.value)} required minLength={2} maxLength={80} autoComplete="name" data-testid="contact-name-input" /></div>
        <div className="space-y-1.5"><Label htmlFor="c-email">Email address</Label><Input id="c-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} required maxLength={120} autoComplete="email" data-testid="contact-email-input" /></div>
      </div>
      <div className="space-y-1.5">
        <Label>Topic</Label>
        <Select value={f.topic} onValueChange={(v) => set("topic", v)}>
          <SelectTrigger data-testid="contact-topic-select"><SelectValue /></SelectTrigger>
          <SelectContent>{TOPICS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`contact-topic-${v}`}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="c-msg">Message</Label>
        <Textarea id="c-msg" value={f.message} onChange={(e) => set("message", e.target.value)} required minLength={10} maxLength={3000} rows={6} data-testid="contact-message-input" />
        <p className="text-xs text-slate-600">{f.message.length}/3000 · Please don't include passwords or card details.</p>
      </div>
      <input type="text" name="website" value={f.website} onChange={(e) => set("website", e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
      {error && <p className="text-sm text-rose-700" role="alert" data-testid="contact-error">{error}</p>}
      <Button type="submit" disabled={busy} className="rounded-full bg-[#222d99] px-6 hover:bg-[#1a237e]" data-testid="contact-submit-btn">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Send message
      </Button>
    </form>
  );
}
